<template>
  <div>
    <v-card>
      <v-card-text>
        <v-form
          id='invoice_account-form'
          @submit.prevent='submitForm'
        >
          <v-row>
            <v-col>

              <div v-if="(!new_element || (new_element && !not_invoice))">
                <input type="radio" id="accounts_list_new" :checked="!order_invoice_id" name="accounts_list" @change="updateElementOnClick($event)" value="new">
                <label for="accounts_list_new" class="pl-1"> {{ $t('invoice_account.new') }} </label>
                <div  v-for="(inv,i) in this.invoice_account"
                      class='mr-4'>
                  <input type="radio" :id="`accounts_list_${i}`" name="accounts_list" :checked="inv.id==order_invoice_id" @change="updateElementOnClick($event)" :value="i">
                  <label :for="`accounts_list_${i}`"><span class="pl-1">{{ inv.last_name }} {{ inv.first_name }}</span>
                    <span class="mb-0">{{inv.company_name}}</span>
                    <span class="mb-0" v-if="inv.vat_number_with_prefix"> - {{inv.vat_number_with_prefix}}</span>
                    <span class="mb-0" v-if="inv.fiscal_code"> - {{inv.fiscal_code}}</span>
                    <span class="mb-0" v-if="inv.address">
                      <span v-if="inv.city_name"> - {{inv.city_name}}</span>
                      <span v-if="inv.province"> - {{inv.province}}</span>
                      <span v-if="inv.country"> - {{inv.country}}</span>
                      <span v-if="inv.zip"> - {{inv.zip}}</span>
                    </span>
                  </label>
                </div>
              </div>

              <v-radio-group v-model='account_type'
                class='mr-4'
                :error-messages='accountTypeErrors'
                @blur='$v.account_type.$touch()'
                @change="$v.account_type.$touch()"
                :disabled="!!order_invoice_id"
              >
                <p class='mr-4'>
                  {{ $t('invoice_account.type_label') }}
                </p>
                <v-radio :label='$t("invoice_account.natural")' :value='accountTypeNaturalValue'/>
                <v-radio :label='$t("invoice_account.legal")' :value='accountTypeLegalValue'/>
              </v-radio-group>
            </v-col>
          </v-row>

          <div v-if="account_type">
            <v-row>
              <v-col cols='12' sm='6'>
                <v-autocomplete
                  v-model='country_id'
                  :disabled="!!order_invoice_id"
                  :items='countries'
                  item-text='name'
                  item-value='id'
                  :label='labelFor("country_id")'
                  :error-messages='countryIdErrors'
                  @input='$v.country_id.$touch()'
                  @blur='$v.country_id.$touch()'
                  prepend-icon='mdi-earth'
                  :search-input.sync="country_name"
                  :loading="isLoadingCountries"
                  hide-no-data
                  persistent-hint
                  :hint="this.$t('hints.search_autocomplete')"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <div v-show="country_id">
              <v-row v-if="isLegal">
                <v-col cols='12' sm='6'>
                  <v-text-field
                    :disabled="!!order_invoice_id"
                    v-model='company_name'
                    :error-messages='companyNameErrors'
                    @input='$v.company_name.$touch()'
                    @blur='$v.company_name.$touch()'
                    :label='labelFor("company_name", {optional: true})'
                    prepend-icon='mdi-domain'
                  />
                </v-col>
                <v-col cols='12' sm='6'>
                  <v-text-field
                    :disabled="!!order_invoice_id"
                    v-model='vat_number'
                    :error-messages='vatNumberErrors'
                    @input='$v.vat_number.$touch()'
                    @blur='$v.vat_number.$touch()'
                    :label='labelFor("vat_number", {optional: true})'
                    prepend-icon='mdi-domain'
                  />
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col cols='12' sm='6'>
                  <v-text-field
                    :disabled="!!order_invoice_id"
                    v-model='first_name'
                    :label='labelFor("first_name", {optional: true})'
                    :error-messages='firstNameErrors'
                    @input='$v.first_name.$touch()'
                    @blur='$v.first_name.$touch()'
                    prepend-icon='mdi-account'
                  />
                </v-col>
                <v-col cols='12' sm='6' md='4'>
                  <v-text-field
                    :disabled="!!order_invoice_id"
                    v-model='last_name'
                    :label='labelFor("last_name", {optional: true})'
                    :error-messages='lastNameErrors'
                    @input='$v.last_name.$touch()'
                    @blur='$v.last_name.$touch()'
                    prepend-icon='mdi-account'
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols='12' sm='6'>
                  <v-text-field
                    :disabled="!!order_invoice_id"
                    v-model='fiscal_code'
                    :label='labelFor("fiscal_code", {optional: true})'
                    :error-messages='fiscalCodeErrors'
                    @input='$v.fiscal_code.$touch()'
                    @blur='$v.fiscal_code.$touch()'
                    prepend-icon='mdi-account-badge'
                    persistent-hint
                    :hint="$t('invoice_account.hints.fiscal_code')"
                  >
                    <template v-slot:append>
                      <v-fade-transition leave-absolute group>
                        <v-progress-circular
                          key='progress'
                          v-if="checkingAvailability"
                          size="24"
                          color="info"
                          indeterminate
                        />
                        <div v-else key='result'>
                          <v-icon key='available' v-if='showAvailabilityIcon && !$v.fiscal_code.$error' color="success">mdi-check-bold</v-icon>
                          <v-icon key='not-available' v-if='showNotAvailabilityIcon' color="error">mdi-alert-circle-outline</v-icon>
                        </div>
                      </v-fade-transition>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <v-row v-if="isLegal">
                <v-col cols="12" sm="6">
                  <v-text-field v-model='email'
                                :disabled="!!order_invoice_id"
                                :label='labelFor("invoice_email", {validation_field: "email", optional: true})'
                                prepend-icon='mdi-email-outline'
                                :error-messages='emailErrors'
                                @input='$v.email.$touch()'
                                @blur='$v.email.$touch()'
                                persistent-hint
                                :hint="$t('invoice_account.hints.invoice_email')"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model='pec'
                                :disabled="!!order_invoice_id"
                                :label='labelFor("pec", {optional: true})'
                                prepend-icon='mdi-email-outline'
                                persistent-hint
                                :hint="$t('invoice_account.hints.pec')"
                  />
                </v-col>
                <v-col cols="12" sm="6" v-if="this.isItaly">
                  <v-text-field v-model='codice_destinatario_sdi'
                                :disabled="!!order_invoice_id"
                                :label='labelFor("codice_destinatario_sdi", {optional: true})'
                                prepend-icon='mdi-account-network'
                                :error-messages='codiceDestinatarioSdiErrors'
                                @input='$v.codice_destinatario_sdi.$touch()'
                                @blur='$v.codice_destinatario_sdi.$touch()'
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols='12' sm='4'>
                  <v-text-field
                    v-model='address'
                    :disabled="!!order_invoice_id"
                    :label='labelFor("address", {optional: true})'
                    :error-messages='addressErrors'
                    @input='$v.address.$touch()'
                    @blur='$v.address.$touch()'
                    prepend-icon='mdi-road'
                  />
                </v-col>

                <v-col cols='12' sm='2' md="2">
                  <v-text-field
                    v-model='street_number'
                    :disabled="!!order_invoice_id"
                    :label='labelFor("street_number", {optional: true})'
                    :error-messages='streetNumberErrors'
                    @input='$v.street_number.$touch()'
                    @blur='$v.street_number.$touch()'
                    prepend-icon='mdi-numeric'
                  />
                </v-col>

                <v-col cols='12' sm='4'>
                  <v-autocomplete
                    v-model='city_id'
                    v-if="country_id && this.isItaly"
                    :items='cities'
                    item-text='name'
                    item-value='id'
                    :disabled="!!order_invoice_id"
                    :label='labelFor("city_id", {optional: true})'
                    :error-messages='cityIdErrors'
                    @input='$v.city_id.$touch()'
                    @blur='$v.city_id.$touch()'
                    prepend-icon='mdi-city'
                    :search-input.sync="city_name"
                    :loading="isLoadingCities"
                    hide-no-data
                    persistent-hint
                    :hint="country_name && this.$t('hints.search_autocomplete')"
                  ></v-autocomplete>
                  <v-text-field
                    v-model='city_name'
                    :disabled="!!order_invoice_id"
                    v-else-if="country_id"
                    :label='labelFor("city_name", {optional: true})'
                    :error-messages='cityNameErrors'
                    @input='$v.city_name.$touch()'
                    @blur='$v.city_name.$touch()'
                    prepend-icon='mdi-city'
                    persistent-hint
                    :hint="country_name && this.$t('hints.write_city_name')"
                  />
                </v-col>

                <v-col cols='12' sm='2' md="2" v-if="city_id">
                  <v-text-field
                    v-model='province'
                    :disabled="!!order_invoice_id"
                    :label='labelFor("province", {optional: true})'
                    :error-messages='provinceErrors'
                    @input='$v.province.$touch()'
                    @blur='$v.province.$touch()'
                    prepend-icon='mdi-puzzle-outline'
                    maxlength="2"
                  />
                </v-col>

                <v-col cols='12' sm='3' md="2" lg="2">
                  <v-text-field
                    v-model='zip'
                    :disabled="!!order_invoice_id"
                    :label='labelFor("zip", {optional: true})'
                    :error-messages='zipErrors'
                    @input='$v.zip.$touch()'
                    @blur='$v.zip.$touch()'
                    prepend-icon='mdi-mailbox-outline'
                  />
                </v-col>
              </v-row>
            </div>

          </div>
        </v-form>
      </v-card-text>
    </v-card>
    <p v-html="$t('commons.notice', {name: (company_name ? company_name : last_name +' '+ first_name), id: id})" class="text-center mt-2" v-if="!new_element"></p>
    <checkin-bottom-navigation>
      <template v-slot:prev-button>
        <v-btn v-if="$v.$anyDirty" @click="undoChanges()">
          {{ $t("commons.cancel") }}
        </v-btn>
        <v-btn v-else @click="goToPrevStepper">
          {{ $t("commons.prev") }}
        </v-btn>
      </template>
      <template v-slot:next-button>
        <v-btn
          v-if='!allowSkipCurrentStep && ($v.$anyDirty || !order_invoice_id || changed)'
          type='submit'
          color='success'
          form='invoice_account-form'
          :loading='submitting'
        >
          {{ (new_element || !order_invoice_id)  ? $t('invoice_account.create_action') : $t('invoice_account.update_action') }}
          <v-icon v-if="submitFailed" right dark>mdi-emoticon-sad-outline</v-icon>
        </v-btn>
        <v-btn v-else color='primary' @click="goToNextStepper">
          {{ allowSkipCurrentStep ? $t('commons.skip') : $t('commons.next') }}
        </v-btn>
      </template>
    </checkin-bottom-navigation>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex'
  import { validationMixin } from 'vuelidate'
  import { required, email, requiredIf, helpers } from 'vuelidate/lib/validators'
  import { reservationMixin } from '../../mixins/reservation_mixin'
  import { invoiceAccountMixin } from '../../mixins/invoice_account_mixin'
  import { accountDataMixin } from '../../mixins/account_data_mixin'
  import { availabilityMixin } from '../../mixins/availability_mixin'
  import { currentUserMixin } from '../../mixins/current_user_mixin'
  import { undoManagementMixin } from '../../mixins/undo_management_mixin'
  import { utilityMixin } from '../../mixins/utility_mixin'

  import CheckinBottomNavigation from '../checkin/checkin_bottom_navigation'

  const FORM_FIELDS = [
    'account_type',
    'country_id',
    'first_name',
    'last_name',
    'company_name',
    'fiscal_code',
    'vat_number',
    'address',
    'street_number',
    'city_id',
    'zip',
    'province'
  ]

  export default {
    name: 'InvoiceAccountForm',
    mixins: [
      validationMixin,
      reservationMixin,
      invoiceAccountMixin,
      accountDataMixin,
      availabilityMixin,
      currentUserMixin,
      undoManagementMixin,
      utilityMixin,
    ],
    components: {
      CheckinBottomNavigation
    },
    props: {
      order: {
        type: Object,
        required: true,
      },
      invoice_account: {
        type: Array,
        required: true,
      },
      reservation: {
        type: Object,
        required: true,
      },
      new_record: {
        type: Boolean,
        required: true,
      },
    },
    // Store description:
    // - this.invoice_account -> object loaded from db
    // - root.fields          -> current invoice_account state
    created() {
      this.initialValues = this.getCurrentValues()
      this.isLoadingCountries = true
      this.$store.dispatch('mappings/fetchCountries', {id: this.country_id} )
        .then( _ => {
          // console.log('created fetchCountries ok', this.countries.length)
        })
        .catch(err => {
          // console.log(err)
          this.$store.dispatch('layout/replaceAlert', {
            type: 'error',
            message: err
          })
        })
        .finally(() => (this.isLoadingCountries = false) )

      this.isLoadingCities = true
      this.$store.dispatch('mappings/fetchCities', {id: this.city_id} )
        .then( _ => {
          // console.log('created fetchCities ok', this.cities.length)
        })
        .catch(err => {
          // console.log(err)
          this.$store.dispatch('layout/replaceAlert', {
            type: 'error',
            message: err
          })
        })
      .finally(() => (this.isLoadingCities = false) )

      // console.log('created check new_record', this.new_record)
      if (this.new_element) this.copyDataFromAlreadyFilledObjects(this.chiefRoomGuest, this.currentUser)
      if (this.isLegal) this.account_type = this.accountTypeLegalValue
    },
    validations() {
      const fiscalCodeRequired = this.isItaly && !this.isLegal
      const re_fiscal_code = /^[A-Z]{6}[0-9]{2}[A-Z]{1}[0-9]{2}[A-Z]{1}[0-9]{3}[A-Z]{1}$/i
      const re_vat_number = /^[0-9]{11}$/
      const vatNumberRequired = this.isLegal
      const codiceDestinatarioSdiRequired = this.isItaly && this.isLegal
      const zipRequired = this.isItaly
      return {
        country_id:       { required },
        account_type:     { required },
        address:          { required },
        street_number:    { required: false},
        zip:                      {
          required,
          zipValidator(value) {
            if (!zipRequired) return true
            return /^\d{5}$/i.test(value)       // zip validation
          },
        },
        // Only italian person
        pec:              { required: requiredIf(_ => { return false }) },
        city_id:          { required: requiredIf(_ => { return this.isItaly }) },
        province:         { required: requiredIf(_ => { return this.isItaly }) },
        // Only foreign countries
        city_name:        { required: requiredIf(_ => { return !this.isItaly }) },
        // Natural person
        first_name:       { required: requiredIf(_ => { return !this.isLegal }) },
        last_name:        { required: requiredIf(_ => { return !this.isLegal }) },
        fiscal_code: {
          required: requiredIf(_ => { return fiscalCodeRequired }),
          fiscalCodeValidator(value) {
            this.resultAvailability = null
            if (!fiscalCodeRequired) return true
            if (this.isLegal) {
              if (!re_fiscal_code.test(value) && !re_vat_number.test(value)) {
                return false
              }
            } else {
              if (!re_fiscal_code.test(value)) {
                return false
              }
            }
            let index = this.index_selected
            if ((!this.invoice_account[index]) || (this.invoice_account[index] && value != this.invoice_account[index].fiscal_code)) {
              this.checkFiscalCodeAvailability(value)
                .then(_ => {
                  this.allowSkipCurrentStep = !this.resultAvailability
                  return true
                })
                .catch( err => {
                  return false
                })
              return true
            } else {
              return true
            }
          },
        },
        // Legal person
        company_name: { required: requiredIf(_ => { return this.isLegal }) },
        vat_number: {
          required: requiredIf(_ => { return this.isLegal }),
          vatNumberValidator(vat_number) {
            return (
              !vatNumberRequired || !this.isItaly ||
              (
                re_vat_number.test(vat_number)
              )
            )
          }
        },
        codice_destinatario_sdi: {
          required: requiredIf(_ => { return codiceDestinatarioSdiRequired }),
          codiceDestinatarioSdiValidator(codice_destinatario_sdi) {
            return (
              !codiceDestinatarioSdiRequired ||
              (
                /^[a-z0-9]{6,7}$/i.test(codice_destinatario_sdi)
              )
            )
          }
        },
        email: {
          required: requiredIf( _ => { return this.isLegal }),
          email
        },
      }
    },
    data() {
      let current_index = "new"
      if (this.invoice_account && this.invoice_account.length>0){
        this.invoice_account.map((ele,i)=>{
          if (ele.id == this.order.invoice_account_id ){
            current_index = i
          }
        })
      }
      return {
        new_element: this.new_record,
        order_invoice_id: this.order.invoice_account_id ? this.order.invoice_account_id : false,
        not_invoice: this.new_record,
        index_selected: current_index,
        changed: false,
        id: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].id : null,
        account_type: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].account_type : null,
        first_name: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].first_name : null,
        last_name: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].last_name : null,
        fiscal_code: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].fiscal_code : '',
        company_name: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].company_name : null,
        email: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].invoice_email : null,
        // email_confirmation: this.invoice_account.invoice_email_confirmation,
        vat_number: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].vat_number : null,
        pec: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].pec : null,
        codice_destinatario_sdi: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].codice_destinatario_sdi : null,
        address: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].address : null,
        street_number: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].street_number : null,
        country_id: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].country_id : null,
        country_name: '',
        city_id: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].city_id : null,
        zip: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].zip : null,
        city_name: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].city_name : '',
        province: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].province : null,
        invoice_account_type_id: (this.invoice_account && this.invoice_account.length>0 && current_index!== "new") ? this.invoice_account[current_index].invoice_account_type_id : null,
        initialValues: {},
        serverSideErrors: {},
        isLoadingCountries: false,
        isLoadingCities: false,
        submitFailed: null,
        allowSkipCurrentStep: false,
      }
    },
    computed: {
      ...mapState('layout', ['submitting']),
      ...mapState('mappings', ['countries', 'cities', 'city']),
      ...mapGetters('mappings', ['getInvoiceAccountTypeByType', 'getCityById']),
      ...mapGetters('profile', [ 'currentUser' ]),

      isLegal() {
        return this.account_type && this.account_type == this.accountTypeLegalValue
      },
      accountTypeLegalValue() {
        return 'LEGAL'
      },
      accountTypeNaturalValue() {
        return 'NATURAL'
      },
    },
    methods: {
      ...mapActions('layout', ['goToPrevStepper', 'goToNextStepper']),
      // TODO: Verificare questo metodo chiamato onchange, sembrerebbe doppio in aggiunta al watch
      fetchCity() {
        this.$v.city_id.$touch()
        this.$store.dispatch('mappings/fetchCity', this.city_id)
          .then(city => {
            // console.log(`city id ${this.city_id}`, city)
            this.province = city.province
          }).catch( error => {
            // console.log(`Cannot fetch city id ${this.city_id}`, error)
        })
      },
      updateElementOnClick(event){
        let index = event.target.value

        if (index == 'new'){
          this.order_invoice_id = false
          this.allowSkipCurrentStep = false
          this.new_element = true
          this.$emit('new-record', this.new_element)
        }else{
          this.order_invoice_id = this.invoice_account[index].id
          this.allowSkipCurrentStep = false
          this.new_element = false
          this.$emit('new-record', this.new_element)
        }
        this.changed = true
        this.index_selected = index == 'new' ? null : index
        this.id = index == 'new' ? null : this.invoice_account[index].id
        this.account_type = index == 'new' ? null : this.invoice_account[index].account_type
        this.first_name = index == 'new' ? null : this.invoice_account[index].first_name
        this.last_name = index == 'new' ? null : this.invoice_account[index].last_name
        this.fiscal_code = index == 'new' ? '' : this.invoice_account[index].fiscal_code
        this.company_name = index == 'new' ? null : this.invoice_account[index].company_name
        this.email = index == 'new' ? null : this.invoice_account[index].invoice_email
        // email_confirmation: this.invoice_account.invoice_email_confirmation,
        this.vat_number = index == 'new' ? null : this.invoice_account[index].vat_number
        this.pec = index == 'new' ? null : this.invoice_account[index].pec
        this.codice_destinatario_sdi = index == 'new' ? null : this.invoice_account[index].codice_destinatario_sdi
        this.address = index == 'new' ? null : this.invoice_account[index].address
        this.street_number = index == 'new' ? null : this.invoice_account[index].street_number
        this.country_id = index == 'new' ? null : this.invoice_account[index].country_id
        this.city_id = index == 'new' ? null : this.invoice_account[index].city_id
        this.zip = index == 'new' ? null : this.invoice_account[index].zip
        this.city_name = index == 'new' ? null : this.invoice_account[index].city_name
        this.province = index == 'new' ? null : this.invoice_account[index].province
        this.invoice_account_type_id = index == 'new' ? null : this.invoice_account[index].invoice_account_type_id

        if (this.new_element) this.copyDataFromAlreadyFilledObjects(this.chiefRoomGuest, this.currentUser)
      },
      labelsPrefix() {
        return 'invoice_account.attributes'
      },
      submitForm() {
        this.$v.$touch()
        if (this.$v.$invalid) return

        if (this.isLegal) {
          this.first_name = null
          this.last_name = null
        } else {
          this.company_name = null
        }

        // console.log(`Invoice account form call createInvoiceAccount ${this.order.id} `)
        this.$store
          .dispatch(`invoice_account/${this.submit_action}InvoiceAccount`, [
            this.order.uuid,
            {
              id: this.id,
              account_type: this.account_type,
              first_name: this.first_name,
              last_name: this.last_name,
              fiscal_code: this.fiscal_code,
              company_name: this.company_name,
              vat_number: this.vat_number,
              invoice_email: this.email,
              pec: this.pec,
              codice_destinatario_sdi: this.codice_destinatario_sdi,
              address: this.address,
              street_number: this.street_number,
              zip: this.zip,
              country_id: this.country_id,
              city_id: this.city_id,
              city_name: this.city_name,
              province: this.province,
              invoice_account_type_id: this.getInvoiceAccountTypeId,
              // invoice_email_confirmation: this.invoice_email_confirmation,
            } ]
          )
          .then(() => {
            this.submitFailed = false
            // console.log(`Invoice account ${this.invoice_account.id} successfully ${this.submit_action}d`)
            // Non serve a causa del redirect
            // this.initialValues = this.getCurrentValues()
            //this.$router.push({ name: 'checkinSummary'})
            this.goToNextStepper()
            this.$store.dispatch('layout/addAlert', {
              type: 'success',
              key: `invoice_account_${this.submit_action}d`,
              // i18n_attributes: {  }
            })
          })
          .catch(error => {
            this.submitFailed = true
            this.serverSideErrors = error.body?.error || error.body?.errors || {}
            if (this.serverSideErrors && this.serverSideErrors.create && this.serverSideErrors.create[0]) this.serverSideErrors = this.serverSideErrors.create[0]
            if (this.serverSideErrors && this.serverSideErrors.update && this.serverSideErrors.update[0]) this.serverSideErrors = this.serverSideErrors.update[0]
            // console.log(`Cannot ${this.submit_action} invoice_account`, this.serverSideErrors)
            if (this.isString(this.serverSideErrors)) {
              // console.log('form serverSideErrors String', this.serverSideErrors)
              this.$store.dispatch('layout/addAlert', {
                type: 'error',
                resource: 'invoice_account',
                key: this.serverSideErrors,
                // i18n_attributes: {  }
              })
            } else {
              // console.log('form serverSideErrors Object', this.serverSideErrors)
              this.$store.dispatch('layout/addAlert', {
                type: 'error',
                resource: 'invoice_account',
                fields: this.serverSideErrors,
              })
            }
          })
      },
      copyDataFromAlreadyFilledObjects(guest, customer) {
        if (this.isLegal) {
          this.company_name = customer.business_name
          this.fiscal_code = customer.fiscal_code
          this.email = customer.email
        } else {
          if (guest.country_of_residence) {
            this.country_name = guest.country_of_residence.name
            this.country_id = guest.country_of_residence.id
            if (guest.country_of_residence.name === 'Italia' || guest.country_of_residence.name === 'Italy') {
              this.city_name = guest.city_of_residence.name
              this.city_id = guest.city_of_residence.id
              this.province = guest.city_of_residence.province
            } else {
              if (guest.city_of_residence) {
                this.city_name = guest.city_of_residence.name
              }
            }
          }
          this.address = guest.address
          this.street_number = guest.street_number
          this.zip = guest.zip
          this.first_name = guest.first_name
          this.last_name = guest.last_name
          this.fiscal_code = guest.fiscal_code
          this.email = guest.email
        }
      },
      getCurrentValues() {
        const values = {}
        FORM_FIELDS.forEach(field => (values[field] = this[field]))
        return values
      },
    },
    watch: {
      country_name (searchName) {
        if (!searchName) {
          searchName = this.country_id    // if name is null try with id
        }
        // console.log('Fetch Countries start with', searchName)
        if (!searchName) return

        // Items have already been requested
        if (this.isLoadingCountries) return

        this.isLoadingCountries = true

        let payload = {}
        // test if searchName is text or id
        if (typeof searchName === 'number') {
          payload = { id: searchName }
        } else {
          payload = { name: searchName }
        }
        this.$store.dispatch('mappings/fetchCountries', payload )
          .then( _ => {
            if (!this.order_invoice_id){
              if (searchName === 'Italia' || searchName === 'Italy' || searchName === 119) {
                this.city_name = ''
              } else {
                this.city_id = null
                this.province = ''
              }
            }

          })
          .catch(err => {
            // console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err
            })
          })
          .finally(() => (this.isLoadingCountries = false))
      },
      city_name (searchName) {
        if (!searchName) {
          searchName = this.city_id    // if name is null try with id
        }

        // console.log('Fetch cities start with', searchName)

        // Items have already been requested
        // Load cities only for italy
        if (!this.isLoadingCities || this.isItaly) {
          this.isLoadingCities = true

          let payload = {}
          // test if searchName is text or id
          if (typeof searchName === 'number') {
            payload = { id: searchName, country_id: this.country_id }
          } else {
            payload = { name: searchName, country_id: this.country_id }
          }
          // Lazily load input items
          this.$store.dispatch('mappings/fetchCities', payload )
            .then( _ => {
              // console.log('Cities successfully fetched')
            })
            .catch(err => {
              // console.log(err)
              this.$store.dispatch('layout/replaceAlert', {
                type: 'error',
                message: err
              })
            })
            .finally(() => (this.isLoadingCities = false))
        }

      },
    },
  }
</script>
